import { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    FluidImage,
    Icon,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    SelectionSetTileImageContainer,
    SelectionSetTileName,
    getTokenCssVar,
} from '@vp/swan';
import type { SelectedValues } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { SelectorCard } from '../SelectorCard';
import type { ColorSelectorOption, ColorSelectorOptions } from './parseOptions';
import type { ColorSelectorReply } from './parseReply';

export type ColorSelectorSelectorViewProps = {
    options: ColorSelectorOptions;
    onSubmit: (reply: ColorSelectorReply) => void;
    /**
     * Asks the assistant to explain the choices. Required, not optional: the footer lays the help
     * and Continue actions out with `space-between`, so an absent help button would silently pull
     * Continue to the left edge rather than degrade gracefully.
     */
    onRequestHelp: () => void;
    /** Maximum number of colors the user may select at once. Matches legacy's default of 3. */
    maxSelections?: number;
};

function toReply(selected: ColorSelectorOption[]): ColorSelectorReply {
    return selected.map(({ value, name, imageUrl }) => ({ value, name, imageUrl }));
}

const TILE_IMAGE_HEIGHT = '40px';
const TILE_MAX_WIDTH_OFFSET = `calc(50% - ${getTokenCssVar('SwanSemSpace4')})`;
const SELECTOR_SCROLL_MAX_HEIGHT = '300px';
const DEFAULT_MAX_SELECTIONS = 3;

/**
 * The color-selector composer selector.
 *
 * Renders only the card. Where that card sits — the composer's background band and centred
 * max-width column — belongs to whatever hosts the composer, exactly as it does for ChatInput.
 * A selector that positioned itself would have to agree with the host on those values, and
 * would double them up when mounted in the composer slot.
 */
export function ColorSelectorSelectorView({
    options,
    onSubmit,
    onRequestHelp,
    maxSelections = DEFAULT_MAX_SELECTIONS,
}: ColorSelectorSelectorViewProps) {
    const [selectedValues, setSelectedValues] = useState<SelectedValues>(
        Object.fromEntries(
            options.options.map((option) => [
                option.value,
                options.preselected?.includes(option.value) ?? false,
            ])
        )
    );
    const [helpRequested, setHelpRequested] = useState(false);
    const headingRef = useRef<HTMLElement>(null);

    useEffect(() => {
        requestAnimationFrame(() => {
            headingRef.current?.focus();
        });
    }, []);

    const title = options.title?.trim() ? options.title : __('Colors to explore');
    const selectedCount = Object.values(selectedValues).filter(Boolean).length;
    const maxReached = selectedCount >= maxSelections;

    const handleSelectedValuesChange = (newSelectedValues: SelectedValues) => {
        setSelectedValues(newSelectedValues);
    };

    const handleContinue = () => {
        const selected = options.options.filter((option) => selectedValues[option.value]);
        if (selected.length > 0) onSubmit(toReply(selected));
    };

    // One help request per selector: the answer arrives as the next assistant turn, and a second
    // identical request before then would just duplicate the message in the thread.
    const handleRequestHelp = () => {
        setHelpRequested(true);
        onRequestHelp();
    };

    const subtitle = `(${__('Select up to {{maxSelections}}', { maxSelections })}${
        selectedCount > 0 ? ` - ${selectedCount}/${maxSelections} ${__('selected')}` : ''
    })`;

    return (
        <SelectorCard
            title={title}
            subtitle={subtitle}
            headingRef={headingRef}
            footer={
                <>
                    <Button
                        skin="tertiary"
                        iconPosition="left"
                        onClick={handleRequestHelp}
                        disabled={helpRequested}
                    >
                        <Icon iconType="aiSupport" />
                        {__('Help me answer')}
                    </Button>
                    <Button
                        skin="secondary"
                        onClick={handleContinue}
                        disabled={selectedCount === 0}
                    >
                        {__('Continue')}
                    </Button>
                </>
            }
        >
            <Box
                style={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    maxHeight: SELECTOR_SCROLL_MAX_HEIGHT,
                    padding: getTokenCssVar('SwanSemSpace1'),
                }}
            >
                <SelectionSet
                    skin="tiles-vertical"
                    variant="multi-select"
                    selectedValues={selectedValues}
                    onSelectedValuesChange={handleSelectedValuesChange}
                    aria-label={title}
                >
                    {options.options.map((option) => (
                        <SelectionSetInput
                            key={option.value}
                            value={option.value}
                            disabled={maxReached && !selectedValues[option.value]}
                        >
                            <SelectionSetLabel
                                style={{
                                    minHeight: 'auto',
                                    height: 'auto',
                                    minWidth: 0,
                                    maxWidth: TILE_MAX_WIDTH_OFFSET,
                                }}
                            >
                                <SelectionSetTileImageContainer
                                    style={{
                                        height: TILE_IMAGE_HEIGHT,
                                    }}
                                >
                                    <FluidImage
                                        src={option.imageUrl}
                                        alt={option.name}
                                        style={{
                                            width: '100%',
                                            height: TILE_IMAGE_HEIGHT,
                                            objectFit: 'fill',
                                            display: 'block',
                                        }}
                                    />
                                </SelectionSetTileImageContainer>
                                <SelectionSetTileName
                                    style={{
                                        textAlign: 'left',
                                        padding: `${getTokenCssVar(
                                            'SwanSemSpace2'
                                        )} ${getTokenCssVar('SwanSemSpace3')} ${getTokenCssVar(
                                            'SwanSemSpace4'
                                        )} ${getTokenCssVar('SwanSemSpace3')}`,
                                        marginTop: getTokenCssVar('SwanSemSpace2'),
                                    }}
                                >
                                    {option.name}
                                </SelectionSetTileName>
                            </SelectionSetLabel>
                        </SelectionSetInput>
                    ))}
                </SelectionSet>
            </Box>
        </SelectorCard>
    );
}
